import { Activity, Bell, House, Plus, Shield, User, Users } from 'lucide-react';
import { Link, NavLink, matchPath, useLocation } from 'react-router';
import { useMe, useUnreadCount } from '../api/queries';
import { BellBadge } from '../components/notifications/BellButton';
import { Avatar } from '../components/ui/Avatar';
import { buttonClass } from '../components/ui/buttonClass';
import { cx } from '../lib/cx';
import { useT } from '../lib/i18n';
import { commonMessages } from '../lib/i18n/messages/common';
import { useAddExpenseTarget } from './addExpenseTarget';
import { useFlowState } from './hooks';

function Item({
  to,
  label,
  icon: Icon,
  badge,
  match,
}: {
  to: string;
  label: string;
  icon: typeof House;
  badge?: number;
  match?: string[];
}) {
  const location = useLocation();
  const extra = match?.some((p) => matchPath(p, location.pathname)) ?? false;
  return (
    <NavLink
      to={to}
      end={to === '/'}
      aria-current={extra ? 'page' : undefined}
      className={({ isActive }) =>
        cx(
          'flex min-h-tap items-center gap-3 rounded-control px-3 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link',
          isActive || extra ? 'bg-accent-soft text-ink' : 'text-ink-muted hover:bg-sunken',
        )
      }
    >
      <Icon className="ic" strokeWidth={1.75} aria-hidden />
      <span className="flex-1">{label}</span>
      {badge !== undefined && <BellBadge count={badge} className="!ring-0" />}
    </NavLink>
  );
}

/** Desktop sidebar, 256px (DESIGN 3.3). */
export function Sidebar() {
  const t = useT(commonMessages);
  const me = useMe();
  const { data: unread = 0 } = useUnreadCount();
  const addTo = useAddExpenseTarget();
  const flowState = useFlowState();
  return (
    <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-line bg-surface px-4 py-6 lg:flex">
      <Link to="/" className="px-3 font-display text-xl font-bold tracking-tight">
        split
      </Link>
      <nav aria-label={t('navMain')} className="mt-6 flex flex-col gap-1">
        <Item to="/" label={t('navHome')} icon={House} match={['/people/:id']} />
        <Item to="/groups" label={t('navGroups')} icon={Users} />
        <Item to="/activity" label={t('navActivity')} icon={Activity} />
        <Item to="/notifications" label={t('navNotifications')} icon={Bell} badge={unread} />
        <Item to="/me" label={t('navMe')} icon={User} />
        {me.role === 'admin' && <Item to="/admin" label={t('navAdmin')} icon={Shield} />}
      </nav>
      <Link
        to={addTo}
        state={flowState}
        className={buttonClass({ variant: 'accent', fullWidth: true, className: 'mt-5' })}
      >
        <Plus className="ic ic-sm" strokeWidth={2.25} aria-hidden />
        {t('addExpense')}
      </Link>
      <div className="flex-1" />
      <Link
        to="/me"
        className="flex min-h-tap items-center gap-3 rounded-control px-2 py-2 hover:bg-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link"
      >
        <Avatar
          size={36}
          person={{
            id: me.id,
            name: me.displayName,
            avatarEmoji: me.avatarEmoji,
            avatarColor: me.avatarColor,
          }}
        />
        <span className="truncate font-semibold">{me.displayName}</span>
      </Link>
    </aside>
  );
}
